import React, { useState, useEffect } from 'react';
import { 
  Lock, Unlock, CheckCircle2, Clock, Play, ChevronRight, 
  Trophy, Target, Calendar, Zap, BookOpen, Timer, AlertCircle
} from 'lucide-react';
import { Button } from './ui/button';
import FocusedLearningModal from './FocusedLearningModal';
import api from '../services/api';
import { toast } from 'sonner';

// 30-Day Roadmap with IXL codes
const THIRTY_DAY_ROADMAP = [
  // Week 1: Foundation
  { day: 1, title: "Linear Equations Basics", ixl_code: "H1A", category: "Algebra", week: 1 },
  { day: 2, title: "Two-Step Equations", ixl_code: "H2A", category: "Algebra", week: 1 },
  { day: 3, title: "Multi-Step Equations", ixl_code: "H3A", category: "Algebra", week: 1 },
  { day: 4, title: "Variables on Both Sides", ixl_code: "H4A", category: "Algebra", week: 1 },
  { day: 5, title: "Equation Word Problems", ixl_code: "H5A", category: "Algebra", week: 1 },
  { day: 6, title: "Inequalities Intro", ixl_code: "H6A", category: "Algebra", week: 1 },
  { day: 7, title: "Week 1 Review & Challenge", ixl_code: "H7R", category: "Review", week: 1 },
  
  // Week 2: Building
  { day: 8, title: "Graphing Linear Equations", ixl_code: "J1A", category: "Graphing", week: 2 },
  { day: 9, title: "Slope-Intercept Form", ixl_code: "J2A", category: "Graphing", week: 2 },
  { day: 10, title: "Point-Slope Form", ixl_code: "J3A", category: "Graphing", week: 2 },
  { day: 11, title: "Parallel & Perpendicular Lines", ixl_code: "J4A", category: "Graphing", week: 2 },
  { day: 12, title: "Systems of Equations Intro", ixl_code: "J5A", category: "Systems", week: 2 },
  { day: 13, title: "Solving by Substitution", ixl_code: "J6A", category: "Systems", week: 2 },
  { day: 14, title: "Week 2 Review & Challenge", ixl_code: "J7R", category: "Review", week: 2 },
  
  // Week 3: Advancing
  { day: 15, title: "Solving by Elimination", ixl_code: "K1A", category: "Systems", week: 3 },
  { day: 16, title: "Systems Word Problems", ixl_code: "K2A", category: "Systems", week: 3 },
  { day: 17, title: "Exponents Basics", ixl_code: "K3A", category: "Exponents", week: 3 },
  { day: 18, title: "Exponent Rules", ixl_code: "K4A", category: "Exponents", week: 3 },
  { day: 19, title: "Scientific Notation", ixl_code: "K5A", category: "Exponents", week: 3 },
  { day: 20, title: "Polynomials Intro", ixl_code: "K6A", category: "Polynomials", week: 3 },
  { day: 21, title: "Week 3 Review & Challenge", ixl_code: "K7R", category: "Review", week: 3 },
  
  // Week 4: Mastery
  { day: 22, title: "Adding & Subtracting Polynomials", ixl_code: "L1A", category: "Polynomials", week: 4 },
  { day: 23, title: "Multiplying Polynomials", ixl_code: "L2A", category: "Polynomials", week: 4 },
  { day: 24, title: "Factoring Basics", ixl_code: "L3A", category: "Factoring", week: 4 },
  { day: 25, title: "Factoring Trinomials", ixl_code: "L4A", category: "Factoring", week: 4 },
  { day: 26, title: "Quadratic Equations", ixl_code: "L5A", category: "Quadratics", week: 4 },
  { day: 27, title: "Quadratic Formula", ixl_code: "L6A", category: "Quadratics", week: 4 },
  { day: 28, title: "Week 4 Review & Challenge", ixl_code: "L7R", category: "Review", week: 4 },
  
  // Final Sprint
  { day: 29, title: "Comprehensive Review", ixl_code: "M1R", category: "Final", week: 5 },
  { day: 30, title: "Final Assessment", ixl_code: "M2F", category: "Final", week: 5 }
];

const DailyRoadmap = ({ user }) => {
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedRep, setSelectedRep] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [nextUnlockTime, setNextUnlockTime] = useState(null);
  const [countdown, setCountdown] = useState('');
  const [equipmentRoomCountdown, setEquipmentRoomCountdown] = useState('');
  const [isInEquipmentRoom, setIsInEquipmentRoom] = useState(false);
  const [huddleLocked, setHuddleLocked] = useState(false);

  useEffect(() => {
    fetchProgress();
  }, []);

  useEffect(() => {
    // Update countdown every second
    if (nextUnlockTime) {
      const interval = setInterval(() => {
        const now = new Date();
        const unlock = new Date(nextUnlockTime);
        const diff = unlock - now;
        
        if (diff <= 0) {
          setCountdown('');
          fetchProgress(); // Refresh to unlock next day
        } else {
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const secs = Math.floor((diff % (1000 * 60)) / 1000);
          setCountdown(`${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`);
        }
      }, 1000);
      
      return () => clearInterval(interval);
    }
  }, [nextUnlockTime]);

  // Equipment Room countdown (24-hour delay after enrollment)
  useEffect(() => {
    if (progress?.equipment_room_unlock_time) {
      const checkEquipmentRoom = () => {
        const now = new Date();
        const unlock = new Date(progress.equipment_room_unlock_time);
        const diff = unlock - now;
        
        if (diff <= 0) {
          setIsInEquipmentRoom(false);
          setEquipmentRoomCountdown('');
        } else {
          setIsInEquipmentRoom(true);
          const hours = Math.floor(diff / (1000 * 60 * 60));
          const mins = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
          const secs = Math.floor((diff % (1000 * 60)) / 1000);
          setEquipmentRoomCountdown(`${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`);
        }
      };
      
      checkEquipmentRoom();
      const interval = setInterval(checkEquipmentRoom, 1000);
      return () => clearInterval(interval);
    }
  }, [progress?.equipment_room_unlock_time]);

  // Check for weekly huddle lock
  useEffect(() => {
    if (progress?.huddle_locked) {
      setHuddleLocked(true);
    } else {
      setHuddleLocked(false);
    }
  }, [progress?.huddle_locked]);

  const fetchProgress = async () => {
    try {
      const token = localStorage.getItem('academy_token');
      const response = await api.get('/academy/daily-progress', {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setProgress(response.data.progress);
        if (response.data.next_unlock_time) {
          setNextUnlockTime(response.data.next_unlock_time);
        }
        // Check equipment room status
        if (response.data.progress.equipment_room_unlock_time) {
          const now = new Date();
          const unlock = new Date(response.data.progress.equipment_room_unlock_time);
          setIsInEquipmentRoom(now < unlock);
        }
      }
    } catch (error) {
      console.error('Failed to fetch progress:', error);
      // Initialize with day 1 if no progress exists
      setProgress({
        current_day: 1,
        completed_days: [],
        streak: 0,
        last_completed_at: null
      });
    } finally {
      setLoading(false);
    }
  };

  const getRepStatus = (day) => {
    if (!progress) return 'locked';
    
    // Equipment Room gate - all reps locked during 24-hour provisioning
    if (isInEquipmentRoom) {
      return 'equipment_room';
    }
    
    // Weekly Huddle lock - next day locked until huddle complete
    if (huddleLocked && day === progress.current_day) {
      return 'huddle_locked';
    }
    
    // Already completed
    if (progress.completed_days?.includes(day)) {
      return 'completed';
    }
    
    // Current day (available)
    if (day === progress.current_day) {
      // Check if today's rep was just completed and we're in lock period
      if (progress.today_completed && countdown) {
        return 'waiting';
      }
      return 'available';
    }
    
    // Future days
    if (day > progress.current_day) {
      return 'locked';
    }
    
    // Past days that weren't completed (missed)
    return 'missed';
  };

  const handleRepClick = (rep) => {
    const status = getRepStatus(rep.day);
    
    if (status === 'equipment_room') {
      toast.info(`Your Equipment Room is being prepared. Access unlocks in ${equipmentRoomCountdown}`);
      return;
    }
    
    if (status === 'huddle_locked') {
      toast.warning('Complete your Weekly Huddle challenge to unlock today\'s rep.');
      return;
    }
    
    if (status === 'completed') {
      toast.info('You already completed this day! Keep up the momentum.');
      return;
    }
    
    if (status === 'locked') {
      toast.warning('This rep is locked. Complete your current day first.');
      return;
    }
    
    if (status === 'waiting') {
      toast.info(`Next rep unlocks in ${countdown}`);
      return;
    }
    
    if (status === 'available' || status === 'missed') {
      setSelectedRep({
        day_number: rep.day,
        title: rep.title,
        ixl_code: rep.ixl_code,
        category: rep.category
      });
      setShowModal(true);
    }
  };

  const handleRepComplete = (data) => {
    setShowModal(false);
    setSelectedRep(null);
    
    // Update local progress
    setProgress(prev => ({
      ...prev,
      current_day: data.next_day || prev.current_day + 1,
      completed_days: [...(prev.completed_days || []), data.completed_day],
      streak: data.streak || prev.streak + 1,
      today_completed: true
    }));
    
    if (data.next_unlock_time) {
      setNextUnlockTime(data.next_unlock_time);
    }
  };

  const getCategoryColor = (category) => {
    const colors = {
      'Algebra': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
      'Graphing': 'bg-green-500/20 text-green-400 border-green-500/30',
      'Systems': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
      'Exponents': 'bg-orange-500/20 text-orange-400 border-orange-500/30',
      'Polynomials': 'bg-pink-500/20 text-pink-400 border-pink-500/30',
      'Factoring': 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
      'Quadratics': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      'Review': 'bg-gray-500/20 text-gray-400 border-gray-500/30',
      'Final': 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30'
    };
    return colors[category] || 'bg-gray-500/20 text-gray-400 border-gray-500/30';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="w-8 h-8 border-2 border-orange-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  const completedCount = progress?.completed_days?.length || 0;
  const totalDays = THIRTY_DAY_ROADMAP.length;
  const progressPercent = Math.round((completedCount / totalDays) * 100);

  return (
    <div className="space-y-6">
      {/* 24-Hour Equipment Room Overlay */}
      {isInEquipmentRoom && (
        <div className="bg-gradient-to-r from-orange-500/10 via-amber-500/10 to-orange-500/10 border-2 border-orange-500/50 rounded-xl p-8 text-center relative overflow-hidden">
          {/* Animated background */}
          <div className="absolute inset-0 bg-gradient-to-r from-orange-500/5 to-amber-500/5 animate-pulse"></div>
          
          <div className="relative z-10">
            <div className="w-20 h-20 bg-orange-500/20 rounded-full flex items-center justify-center mx-auto mb-4 border-2 border-orange-500/50">
              <Timer className="w-10 h-10 text-orange-500" />
            </div>
            
            <h3 className="text-2xl font-black text-white mb-2">
              EQUIPMENT ROOM
            </h3>
            <p className="text-orange-500 font-bold text-lg mb-4">
              Provisioning Your Credentials...
            </p>
            
            {/* Countdown Timer */}
            <div className="bg-black/50 rounded-xl p-6 inline-block mb-4">
              <p className="text-gray-400 text-sm mb-2 uppercase tracking-wider">Combine Access Unlocks In</p>
              <p className="text-5xl font-mono font-black text-orange-500">
                {equipmentRoomCountdown || '00:00:00'}
              </p>
            </div>
            
            <p className="text-gray-400 max-w-md mx-auto">
              We're calibrating your IXL Teacher Account and preparing your personalized learning path. 
              Your <span className="text-orange-500 font-bold">Day 1 Rep</span> will unlock automatically when the timer hits zero.
            </p>
            
            <div className="mt-6 flex items-center justify-center gap-2 text-gray-500 text-sm">
              <AlertCircle className="w-4 h-4" />
              <span>Check your email for your "Credentials Live" notification</span>
            </div>
          </div>
        </div>
      )}
      
      {/* Weekly Huddle Lock Banner */}
      {huddleLocked && !isInEquipmentRoom && (
        <div className="bg-gradient-to-r from-purple-500/10 to-purple-600/10 border border-purple-500/50 rounded-xl p-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
              <Lock className="w-6 h-6 text-purple-500" />
            </div>
            <div className="flex-1">
              <h4 className="text-white font-bold">Weekly Huddle Required</h4>
              <p className="text-gray-400 text-sm">Complete this week's Family Huddle challenge to unlock your next rep.</p>
            </div>
            <Button 
              className="bg-purple-500 hover:bg-purple-600 text-white font-bold"
              onClick={() => toast.info('Navigate to Family Huddle section')}
            >
              Go to Huddle
            </Button>
          </div>
        </div>
      )}

      {/* Progress Overview */}
      <div className="bg-gradient-to-r from-gray-900 to-gray-800 border border-gray-800 rounded-xl p-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h3 className="text-white font-black text-xl mb-1 flex items-center gap-2">
              <Target className="w-5 h-5 text-orange-500" />
              30-DAY HIGH PERFORMANCE ROADMAP
            </h3>
            <p className="text-gray-400 text-sm">
              Your daily IXL training assignments • Complete each rep to unlock the next
            </p>
          </div>
          
          <div className="flex items-center gap-6">
            {/* Streak */}
            <div className="text-center">
              <div className="flex items-center gap-1 text-orange-500">
                <Zap className="w-5 h-5" />
                <span className="text-2xl font-black">{progress?.streak || 0}</span>
              </div>
              <p className="text-gray-500 text-xs">Day Streak</p>
            </div>
            
            {/* Progress */}
            <div className="text-center">
              <div className="flex items-center gap-1 text-green-500">
                <CheckCircle2 className="w-5 h-5" />
                <span className="text-2xl font-black">{completedCount}/{totalDays}</span>
              </div>
              <p className="text-gray-500 text-xs">Completed</p>
            </div>
          </div>
        </div>
        
        {/* Progress Bar */}
        <div className="mt-4">
          <div className="flex justify-between text-xs text-gray-500 mb-1">
            <span>Progress</span>
            <span>{progressPercent}%</span>
          </div>
          <div className="h-2 bg-gray-800 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-orange-500 to-green-500 transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
        
        {/* Next Unlock Countdown */}
        {countdown && progress?.today_completed && (
          <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/30 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5 text-orange-500" />
                <span className="text-gray-300">
                  Day Complete! Next Rep Unlocks In:
                </span>
              </div>
              <span className="text-orange-500 font-mono text-xl font-bold">
                {countdown}
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Weekly View */}
      {[1, 2, 3, 4, 5].map(week => {
        const weekReps = THIRTY_DAY_ROADMAP.filter(r => r.week === week);
        const weekName = week === 5 ? 'Final Sprint' : `Week ${week}`;
        const weekSubtitle = {
          1: 'Foundation',
          2: 'Building',
          3: 'Advancing',
          4: 'Mastery',
          5: 'Finish Strong'
        }[week];
        
        return (
          <div key={week} className="border border-gray-800 rounded-xl overflow-hidden bg-gray-900/30">
            <div className="px-4 py-3 bg-gray-800/50 border-b border-gray-800 flex items-center gap-3">
              <Calendar className="w-5 h-5 text-orange-500" />
              <div>
                <h4 className="text-white font-bold">{weekName}</h4>
                <p className="text-gray-500 text-xs">{weekSubtitle}</p>
              </div>
            </div>
            
            <div className="p-4 grid gap-2">
              {weekReps.map(rep => {
                const status = getRepStatus(rep.day);
                
                return (
                  <button
                    key={rep.day}
                    onClick={() => handleRepClick(rep)}
                    disabled={status === 'locked' && countdown}
                    className={`w-full flex items-center gap-4 p-3 rounded-lg transition-all ${
                      status === 'completed' 
                        ? 'bg-green-500/10 border border-green-500/30 cursor-default'
                        : status === 'available'
                        ? 'bg-orange-500/10 border-2 border-orange-500 hover:bg-orange-500/20 cursor-pointer'
                        : status === 'waiting'
                        ? 'bg-gray-800/50 border border-gray-700 cursor-not-allowed opacity-60'
                        : status === 'missed'
                        ? 'bg-red-500/10 border border-red-500/30 hover:bg-red-500/20 cursor-pointer'
                        : 'bg-gray-800/30 border border-gray-800 cursor-not-allowed opacity-50'
                    }`}
                    data-testid={`day-${rep.day}-rep`}
                  >
                    {/* Status Icon */}
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${
                      status === 'completed' ? 'bg-green-500/20' :
                      status === 'available' ? 'bg-orange-500/20' :
                      status === 'waiting' ? 'bg-gray-700' :
                      status === 'missed' ? 'bg-red-500/20' :
                      'bg-gray-800'
                    }`}>
                      {status === 'completed' ? (
                        <CheckCircle2 className="w-5 h-5 text-green-500" />
                      ) : status === 'available' ? (
                        <Play className="w-5 h-5 text-orange-500" />
                      ) : status === 'waiting' ? (
                        <Clock className="w-5 h-5 text-gray-500" />
                      ) : status === 'missed' ? (
                        <BookOpen className="w-5 h-5 text-red-500" />
                      ) : (
                        <Lock className="w-5 h-5 text-gray-600" />
                      )}
                    </div>
                    
                    {/* Day Info */}
                    <div className="flex-1 text-left">
                      <div className="flex items-center gap-2">
                        <span className={`font-bold ${
                          status === 'completed' ? 'text-green-400' :
                          status === 'available' ? 'text-white' :
                          status === 'waiting' ? 'text-gray-500' :
                          status === 'missed' ? 'text-red-400' :
                          'text-gray-600'
                        }`}>
                          Day {rep.day}
                        </span>
                        <span className={`px-2 py-0.5 rounded text-xs font-mono ${
                          status === 'locked' || status === 'waiting' ? 'bg-gray-800 text-gray-600' : 'bg-gray-800 text-orange-500'
                        }`}>
                          {rep.ixl_code}
                        </span>
                      </div>
                      <p className={`text-sm ${
                        status === 'completed' ? 'text-green-400/70' :
                        status === 'available' ? 'text-gray-300' :
                        status === 'waiting' ? 'text-gray-600' :
                        status === 'missed' ? 'text-red-400/70' :
                        'text-gray-700'
                      }`}>
                        {rep.title}
                      </p>
                    </div>
                    
                    {/* Category Badge */}
                    <span className={`px-2 py-1 rounded text-xs font-medium border ${getCategoryColor(rep.category)}`}>
                      {rep.category}
                    </span>
                    
                    {/* Arrow */}
                    {(status === 'available' || status === 'missed') && (
                      <ChevronRight className="w-5 h-5 text-orange-500" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        );
      })}

      {/* Focused Learning Modal */}
      <FocusedLearningModal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          setSelectedRep(null);
        }}
        dailyRep={selectedRep}
        onComplete={handleRepComplete}
        userId={user?.id}
      />
    </div>
  );
};

export default DailyRoadmap;
